<template>
    <section class="uk-risk-warning">
        <img
            :src="icon"
            class="uk-risk-warning__image"
            width="45%"
            alt="uk-risk-warning-image-icon"
        >
        <div class="uk-risk-warning__content">
            <p class="uk-risk-warning__title uk-risk-warning__text">
                {{ title }}
            </p>
            <div v-if="activeStep === STEP.WARNING"
                class="uk-risk-warning__text--wrapper"
            >
                <p class="uk-risk-warning__text">
                    {{ $t('jurisdictionRequirements.ukRiskWarning.description') }}
                </p>
                <div class="uk-risk-warning__link-wrapper">
                    <translation-string :translation="warningText"
                        class="uk-risk-warning__text"
                    />
                </div>
                <p class="uk-risk-warning__text uk-risk-warning__text--bold">
                    {{ $t('jurisdictionRequirements.ukRiskWarning.agreeByProceeding') }}
                </p>
            </div>
            <div v-else-if="activeStep === STEP.NAME_FORM">
                <custom-input
                    v-model="firstNameInput"
                    name="firstNameInput"
                    class="uk-risk-warning__input"
                    :label="$t('profile.identityInfoPage.firstName')"
                    :placeholder="$t('profile.identityInfoPage.firstNameHint')"
                    required
                    :is-valid.sync="isFirstNameValid"
                />
                <custom-input
                    v-model="lastNameInput"
                    name="lastNameInput"
                    class="uk-risk-warning__input"
                    :label="$t('profile.identityInfoPage.lastName')"
                    :placeholder="$t('profile.identityInfoPage.lastNameHint')"
                    required
                    :is-valid.sync="isLastNameValid"
                />
            </div>
            <p v-else
                class="uk-risk-warning__text"
            >
                <translation-string :translation="$t('jurisdictionRequirements.ukRiskWarning.denied')"
                    automationText="uk-risk-warning-description"
                />
            </p>
        </div>
        <div class="buttons">
            <loading-spinner v-if="activeStep === STEP.WARNING && isSubmitLoading"
                class="uk-risk-warning__spinner"
            />
            <template v-else-if="activeStep === STEP.WARNING">
                <button type="button"
                    class="uk-risk-warning__button"
                    color="green"
                    @click="submitRequirement"
                >
                    {{ $t('global.button.iAgree') }}
                </button>
                <button type="button"
                    class="uk-risk-warning__button uk-risk-warning__button--secondary"
                    color="white"
                    @click="nextStep"
                >
                    {{ $t('global.button.decline') }}
                </button>
            </template>
            <button v-else-if="activeStep === STEP.NAME_FORM"
                type="button"
                :class="['uk-risk-warning__button',
                         {'uk-risk-warning__button--disabled': isNextStepButtonDisabled }
                ]"
                :disabled="isNextStepButtonDisabled"
                @click="goToWarningPage"
            >
                {{ $t('global.button.continue') }}
            </button>
            <button v-else
                type="button"
                class="uk-risk-warning__button"
                @click="previousStep"
            >
                {{ $t('global.button.back') }}
            </button>
        </div>
    </section>
</template>

<script>
// 3rd party
import {
    mapActions, mapGetters,
} from 'vuex';

// Types
import localStoreTypes from '@webapp/store/types.js';
import {
    ServerConfigurationProps,
} from '@shared/types/config';

// Components
import translationString from '@webapp/components/translation-string.vue';
import customInput from '@webapp/components/form/custom-input.vue';
import loadingSpinner from '@webapp/components/loading-spinner/loading-spinner.vue';

const STEP = {
    NAME_FORM: 0,
    WARNING: 1,
    BLOCKING_PAGE: 2,
};

export default {
    name: 'uk-risk-warning',
    components: {
        translationString,
        loadingSpinner,
        customInput,
    },
    props: {
        firstName: {
            type: String,
            required: true,
        },
        lastName: {
            type: String,
            required: true,
        },
        submit: {
            type: Function,
            required: true,
        },
        onSuccess: {
            type: Function,
            required: true,
        },
    },
    data() {
        return {
            isSubmitLoading: false,
            firstNameInput: '',
            isFirstNameValid: false,
            lastNameInput: '',
            isLastNameValid: false,
            activeStep: 0,
            STEP,
        };
    },
    computed: {
        ...mapGetters([
            'userDetails',
            'getLatestError',
            'getLatestValidationError',
        ]),

        ...mapGetters('serverConfig', ['serverConfigByKey']),

        title() {
            return this.activeStep === STEP.NAME_FORM
                ? this.$t('jurisdictionRequirements.ukRiskWarning.enterFullName')
                : this.$t('jurisdictionRequirements.ukRiskWarning.riskWarning');
        },

        warningText() {
            return this.$t('jurisdictionRequirements.ukRiskWarning.warning', {
                fullName: this.fullName,
                link: this.serverConfigByKey(ServerConfigurationProps.ukRiskWarningLink),
            });
        },

        fullName() {
            return `${this.firstNameInput} ${this.lastNameInput}`;
        },

        isNextStepButtonDisabled() {
            return !this.isFirstNameValid || !this.isLastNameValid;
        },

        icon() {
            return this.activeStep === STEP.BLOCKING_PAGE
                ? require('@webapp/assets/statuses/denied.webp')
                : require('@webapp/assets/warning-yellow.webp');
        },
    },
    watch: {
        getLatestError: 'onError',
        getLatestValidationError: 'onError',
    },
    mounted() {
        this.firstNameInput = this.firstName || '';
        this.lastNameInput = this.lastName || '';

        if (this.firstName && this.lastName) {
            this.nextStep();
        }
    },
    methods: {
        ...mapActions({
            closeModalSheet: localStoreTypes.RESET_MODAL,
        }),

        async submitRequirement() {
            this.isSubmitLoading = true;

            await this.submit({
                firstName: this.firstNameInput,
                lastName: this.lastNameInput,
            });

            this.onSuccess();
        },

        onError() {
            this.$notify({
                type: 'error',
                text: this.$t('errors.wrongJurisdictionRequiremenets'),
            });

            this.isSubmitLoading = false;
        },

        goToWarningPage() {
            if (this.isFirstNameValid && this.isLastNameValid) {
                this.nextStep();
            }
        },

        previousStep() {
            this.activeStep--;
        },

        nextStep() {
            this.activeStep++;
        },
    },
};
</script>

<style lang="scss" scoped>
.uk-risk-warning {
    display: flex;
    flex-direction: column;
    padding: 0 2rem;

    &__title {
        font-size: 1.6rem;
        font-weight: bold;
        margin-bottom: 20px;
    }

    &__image {
        text-align: center;
        width: 40%;
        display: block;
        margin: 0 auto 20px;
    }

    &__content {
        margin-bottom: 24px;
    }

    &__spinner {
        width: 20px;
    }

    &__link-wrapper {
        border: 2px #cdcdcd solid;
        border-radius: 20px;
        padding: 10px;
        background-color: #fdfdfd;
    }

    &__text {
        color: $cyprus-color;
        font-size: 15px;
        text-align: center;
        margin-bottom: 20px;

        &--bold {
            font-weight: bold;
        }

        &--wrapper {
            text-align: center;
        }
    }

    &__input {
        text-align: initial;
        margin-bottom: 20px;
    }

    &__button {
        display: block;
        margin: 0 auto 1rem;
        height: 3.5rem;
        line-height: 3.5rem;
        width: 20rem;
        border-radius: 0.5rem;
        background: $lima-color;
        text-align: center;
        font-size: 1.4rem;
        color: $daintree-color;
        border: none;
        align-self: flex-end;

        @include min-width(md) {
            width: 21.9rem;
        }

        &--disabled {
            opacity: 0.3;
            cursor: default;
        }

        &:hover {
            cursor: pointer;
            opacity: 0.8;
        }

        &--secondary {
            color: $daintree-color;
            display: block;
            background: none;
            margin-bottom: 0;
            border: 0;
        }
    }
}
</style>